import { useCallback } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";

import { ensureOnboardingOverviewData } from "@/api/onboarding/onboardingQueries";
import { getModuleConfig } from "@/components/onboarding/configs/getModuleConfig";
import { useMap } from "@/hooks/map/core/useMapContext";
import { AppDispatch, RootState } from "@/store";
import {
  enterOnboardingMode,
  exitOnboardingMode
} from "@/store/slice/onboardingSlice";

import {
  buildOnboardingSnapshot,
  captureMapCamera,
  getSnapshotDrawnPolygonFeatures,
  resetOnboardingMapLayers,
  resetOnboardingSlices,
  resolveModuleId,
  restoreDrawnPolygons,
  restoreMapCamera,
  restoreOnboardingMapLayers,
  restoreSnapshot
} from "./utils";

export function useEnterOnboardingMode() {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    map,
    deleteAllFeatures,
    resetAllFeatureStates,
    handleRemoveSourceLayer
  } = useMap();

  return useCallback(
    async (moduleCode: string) => {
      const config = getModuleConfig(moduleCode);

      if (!config) {
        console.warn(
          `[useEnterOnboardingMode] no module config for code "${moduleCode}"`
        );
        return;
      }

      // A tour's steps target DOM elements that only exist on the page it runs
      // on (config.route, default "/" — map tools, analysis queue, dataset
      // panels all live on the homepage). Redirect there before seeding so every
      // step can find its target, and remember where the user came from so exit
      // can return them. Captured before navigating; the target page mounts
      // while the cache below loads.
      const entryPath = location.pathname;
      const tourRoute = config.route ?? "/";

      if (entryPath !== tourRoute) navigate(tourRoute);

      // Login/auth flow warms this cache. Keep ensureQueryData here as a
      // fallback for direct entry or a failed background preload.
      const [modulesResp] = await ensureOnboardingOverviewData(queryClient);

      const moduleId = resolveModuleId(modulesResp, moduleCode);

      if (!moduleId) {
        console.warn(
          `[useEnterOnboardingMode] module code "${moduleCode}" not in BE module list`
        );
        return;
      }

      // Snapshot before resetting so exitOnboardingMode can restore real state. The
      // live map camera is captured here too — it lives on the map instance,
      // not Redux, so resetOnboardingSlices alone can't put it back.
      //
      // store.getState() rather than useSelector: this runs after an async
      // preload, so snapshot the current pre-reset state instead of a selector
      // value captured by an earlier render. Reading imperatively also avoids
      // subscribing the hook to the churning dataset slice, which would
      // re-render and re-create this callback on every dataset change.
      const state = store.getState();
      const snapshot = buildOnboardingSnapshot(
        state,
        captureMapCamera(map),
        entryPath
      );

      toast.dismiss();

      resetOnboardingSlices(dispatch);
      // Clear the imperative map view: drawn polygons, feature highlights and
      // camera orientation/zoom outlive a Redux reset, so wipe them explicitly
      // for a clean Onboarding canvas.
      deleteAllFeatures();
      resetAllFeatureStates();
      // Dataset sources/layers are imperative too — resetOnboardingSlices clears
      // the dataset slice, but the rendered datasets stay painted on the map
      // until their sources/layers are removed. Read from the pre-reset state.
      resetOnboardingMapLayers(state.dataset.datasets, handleRemoveSourceLayer);
      // Flip into Onboarding mode.
      dispatch(enterOnboardingMode({ moduleCode, snapshot }));
    },
    [
      dispatch,
      store,
      queryClient,
      navigate,
      location.pathname,
      map,
      deleteAllFeatures,
      resetAllFeatureStates,
      handleRemoveSourceLayer
    ]
  );
}

export function useExitOnboardingMode() {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    map,
    deleteAllFeatures,
    renderDrawPolygon,
    handleRemoveSourceLayer,
    handleResetLayerData,
    handleResetLayerOrder
  } = useMap();

  const snapshot = useSelector((state: RootState) => state.onboarding.snapshot);

  return useCallback(() => {
    if (snapshot) {
      const currentDatasets = store.getState().dataset.datasets;

      // Push the captured pre-Onboarding slice state back into Redux. enterOnboardingMode
      // reset + seeded every snapshotted slice, so without this the user's real
      // data never comes back on exit.
      restoreSnapshot(dispatch, snapshot);
      // Fly the live map back to the view captured at Onboarding entry, in step with
      // the slice restore above.
      restoreMapCamera(map, snapshot.mapCamera);
      // Dataset sources/layers were removed on enter (resetOnboardingMapLayers).
      // restoreSnapshot only puts the dataset slice back in Redux — the imperative
      // MapLibre sources/layers must be repainted and reordered from the snapshot
      // so the user's rendered datasets reappear on exit.
      restoreOnboardingMapLayers(
        snapshot.dataset,
        currentDatasets,
        handleRemoveSourceLayer,
        handleResetLayerData,
        handleResetLayerOrder
      );
      // The draw control was cleared on enter; restoreSnapshot only puts
      // polygon state back in Redux. Redraw directly from the snapshot so saved,
      // draft and custom dataset polygons all survive the Onboarding round trip.
      restoreDrawnPolygons(
        deleteAllFeatures,
        renderDrawPolygon,
        getSnapshotDrawnPolygonFeatures(snapshot)
      );
    }
    dispatch(exitOnboardingMode());

    // Onboarding entry redirected the user to the tour's route; send them back
    // to where they started. Skip only when the entry path already matches the
    // current location — comparing against the live path (not a hardcoded "/")
    // handles tours whose route isn't the homepage (e.g. Bring Your Own Data
    // runs on /dataset-management, so an entry from "/" must redirect back).
    // Null entryPath (a snapshot from before this field existed) is a no-op.
    if (snapshot?.entryPath && snapshot.entryPath !== location.pathname) {
      navigate(snapshot.entryPath);
    }
  }, [
    dispatch,
    store,
    navigate,
    location.pathname,
    snapshot,
    map,
    deleteAllFeatures,
    renderDrawPolygon,
    handleRemoveSourceLayer,
    handleResetLayerData,
    handleResetLayerOrder
  ]);
}
